import { Controller } from "@hotwired/stimulus";
import { nanoid } from "nanoid";

window.pagefind = require("../pagefind.js");

export default class extends Controller {
  static targets = [
    "container",
    "empty",
    "placeholder",
    "searching",
    "count",
    "prev",
    "next",
    "close",
    "label",
    "input",
  ];
  static values = { q: String, id: String, count: Number };

  countValueChanged(value) {
    if (!this.hasCountTarget) return;
    if (value === undefined) return;

    this.countTarget.innerHTML = value;
  }

  relativeUrl(url) {
    return url.toString().replace(window.location.origin, "");
  }

  active(event) {
    for (const labelTarget of this.labelTargets) {
      const labelId = event.target.getAttribute("aria-labelledby");
      const labelStatus = labelTarget.id === labelId;

      for (const item of labelTarget.querySelectorAll(
        "[data-active-add-class],[data-active-remove-class]"
      )) {
        if (labelStatus) {
          if ("activeRemoveClass" in item.dataset)
            item.classList.remove(...item.dataset.activeRemoveClass.split(" "));
          if ("activeAddClass" in item.dataset)
            item.classList.add(...item.dataset.activeAddClass.split(" "));
        } else {
          if ("activeRemoveClass" in item.dataset)
            item.classList.add(...item.dataset.activeRemoveClass.split(" "));
          if ("activeAddClass" in item.dataset)
            item.classList.remove(...item.dataset.activeAddClass.split(" "));
        }
      }
    }
  }

  toggleLabelClass(event) {
    for (const labelTarget of this.labelTargets) {
      const labelId = event.target.dataset.labelId;

      if (labelTarget.id !== labelId) continue;

      const labelInputs = [];

      for (const inputTarget of this.inputTargets) {
        if (inputTarget.dataset.labelId !== labelId) continue;

        labelInputs.push(inputTarget);
      }

      const labelStatus = labelInputs.some((x) => {
        switch (x.type) {
          case "checkbox":
          case "radio":
            return x.checked;
          default:
            return x.value.trim().length;
        }
      });

      for (const item of labelTarget.querySelectorAll(
        "[data-label-add-class],[data-label-remove-class]"
      )) {
        if (labelStatus) {
          if ("labelRemoveClass" in item.dataset)
            item.classList.remove(...item.dataset.labelRemoveClass.split(" "));
          if ("labelAddClass" in item.dataset)
            item.classList.add(...item.dataset.labelAddClass.split(" "));
        } else {
          if ("labelRemoveClass" in item.dataset)
            item.classList.add(...item.dataset.labelRemoveClass.split(" "));
          if ("labelAddClass" in item.dataset)
            item.classList.remove(...item.dataset.labelAddClass.split(" "));
        }
      }
    }
  }

  async connect() {
    this.idValue = window.location.hash;

    if (this.idValue) {
      const data = JSON.parse(window.localStorage[this.idValue] || "{}");

      if (typeof data.count === "number") this.countValue = data.count;

      // Encontrar la posición del resultado en los resultados actuales.
      const pos = data?.resultUrls?.indexOf(
        decodeURIComponent(window.location.pathname)
      );
      const url = new URL(window.location.origin);

      url.hash = this.idValue;

      // Volver al buscador trayéndolo desde la caché, para que el
      // formulario de búsqueda y resultados quedan llenos.
      if (this.hasCloseTarget) {
        const searchUrl = new URL(data.searchUrl || window.location.origin);

        this.closeTarget.setAttribute("href", this.relativeUrl(searchUrl));
        this.closeTarget.dataset.turboAction = "restore";
      }

      if (pos !== undefined) {
        if (this.hasNextTarget) {
          const nextUrl = data.resultUrls[pos + 1];

          if (nextUrl) {
            url.pathname = nextUrl;
            this.nextTarget
              .querySelector("a[href]")
              ?.setAttribute("href", this.relativeUrl(url));
          } else {
            this.nextTarget.setAttribute("hidden", true);
            this.nextTarget.classList.add("invisible");
          }
        }

        if (this.hasPrevTarget) {
          const prevUrl = data.resultUrls[pos - 1];

          if (prevUrl) {
            url.pathname = prevUrl;
            this.prevTarget
              .querySelector("a[href]")
              ?.setAttribute("href", this.relativeUrl(url));
          } else {
            this.prevTarget.setAttribute("hidden", true);
            this.prevTarget.classList.add("invisible");
          }
        }
      } else {
        this.nextTarget.setAttribute("hidden", true);
        this.prevTarget.setAttribute("hidden", true);
        this.nextTarget.classList.add("invisible");
        this.prevTarget.classList.add("invisible");
      }
    }

    await window.pagefind.options({
      baseUrl: "",
      bundlePath: "pagefind/",
      excerptLength: 0,
    });

    await window.pagefind.init();
    this.filters = await window.pagefind.filters();

    if (window.location.search) {
      const searchParams = new URLSearchParams(window.location.search);

      if (searchParams.has("q")) {
        const form = this.element.querySelector("form");

        if ("q" in form.elements) {
          form.elements.q.value = searchParams.get("q");
          this.search({ target: form });
        }
      }
    }
  }

  async search(event) {
    const form = event.target;
    const formData = new FormData(form);
    const formDataSerialized = new URLSearchParams(formData).toString();
    const filters = {};

    for (const key of formData.keys()) {
      if (!this.filters[key]) continue;

      const any = formData.getAll(key);

      if (any.length === 0) continue;

      filters[key] = { any };
    }

    const q = formData.get("q") || null;

    this.qValue = q;
    this.formDisable(form, true);
    this.searchingTargets.forEach((x) => x.classList.remove("d-none"));

    // TODO: FormData como objeto
    this.searchResults = await window.pagefind.search(q, { filters });
    const count = this.searchResults.results.length;

    this.countValue = count;
    this.containerTarget.innerHTML = "";
    this.emptyTargets.forEach((x) => x.classList.add("d-none"));
    this.searchingTargets.forEach((x) => x.classList.add("d-none"));

    if (count) {
      this.idValue = `#${nanoid()}`;

      const results = await this.resolveResults(this.searchResults.results);
      const resultUrls = results.map((x) => x.url.replace("/indexed", ""));
      const searchUrl = window.location.href;

      // Mientras separamos el nanoid podemos volver acá
      window.localStorage.setItem(
        this.idValue,
        JSON.stringify({
          searchUrl,
          count,
          q,
          filters,
          formDataSerialized,
          resultUrls,
        })
      );

      this.renderResults(results);
    } else {
      this.emptyTargets.forEach((x) => x.classList.remove("d-none"));
    }

    this.formDisable(form, false);
  }

  /*
   * Resuelve los datos de todos los resultados
   *
   * @param results [Array<Result>]
   * @return [Promise]
   */
  async resolveResults(results) {
    return await Promise.all(results.map((r) => r.data()));
  }

  async reset(event) {
    this.searchResults = null;

    for (const item of this.element.querySelectorAll(
      "[data-label-add-class],[data-label-remove-class],[data-active-remove-class],[data-active-add-class]"
    )) {
      if ("labelRemoveClass" in item.dataset)
        item.classList.add(...item.dataset.labelRemoveClass.split(" "));
      if ("labelAddClass" in item.dataset)
        item.classList.remove(...item.dataset.labelAddClass.split(" "));
      if ("activeRemoveClass" in item.dataset)
        item.classList.add(...item.dataset.activeRemoveClass.split(" "));
      if ("activeAddClass" in item.dataset)
        item.classList.remove(...item.dataset.activeAddClass.split(" "));
    }
  }

  updateHistory() {
    const q = this.qValue;
    const query = new URLSearchParams({ q });

    window.history.pushState({ q }, q, `?${query.toString()}`);
  }

  formDisable(form, disable) {
    Array.from(form.elements).forEach((x) => (x.disabled = disable));
  }

  renderResults(results) {
    results.forEach((result) => this.renderResult(result));
  }

  renderResult(result) {
    const placeholder =
      this.placeholderTarget.content.firstElementChild.cloneNode(true);

    this.containerTarget.appendChild(placeholder);

    // TODO: Gestión de errores y cancelar peticiones
    fetch(result.url)
      .then((response) => response.text())
      .then((body) => {
        const template = document.createElement("template");
        template.innerHTML = body;
        const result = template.content.firstElementChild;

        result.querySelectorAll("[href]").forEach((x) => {
          let url;

          if (x.href.includes("://")) {
            url = new URL(x.href);
          } else {
            url = new URL(window.location.origin);
            url.pathname = x.href;
          }

          url.hash = this.idValue;

          x.href = this.relativeUrl(url);
        });

        placeholder.replaceWith(result);
      })
      .catch(console.error);
  }
}
